import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  Download,
  RefreshCw,
  Sparkles,
  X
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { formatDate, formatDuration } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { PageLoading } from "../components/Loading";

export function DiscoverPage() {
  useDocumentTitle("TED 放映单");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog });
  const sync = useMutation({
    mutationFn: api.syncCatalog,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalog"] })
  });
  const add = useMutation({
    mutationFn: api.addCatalogItem,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["talks"] });
      navigate(`/talks/${result.talk.id}`);
    }
  });

  if (catalog.isLoading) return <PageLoading label="正在翻找 TED 放映单" />;
  const catalogSource = catalog.data?.[0]?.sourceType;
  const sourceLabel = catalogSource === "ted" ? "TED 官网" : "YouTube";

  return (
    <div className="page-frame discover-page">
      <header className="discover-header">
        <div>
          <span className="eyebrow">TED SCREENING LIST</span>
          <h1>TED 放映单</h1>
          <p>
            {sourceLabel}最新演讲的入口。加入学习库后，云端会下载完整视频并优先生成双语字幕。
          </p>
        </div>
        <div className="discover-actions">
          {catalog.data?.[0]?.publishedAt && (
            <span className="last-sync">最近目录更新于 {formatDate(catalog.data[0].publishedAt)}</span>
          )}
          <Button
            variant="primary"
            icon={<RefreshCw size={16} />}
            loading={sync.isPending}
            onClick={() => sync.mutate()}
          >
            同步目录
          </Button>
        </div>
      </header>

      {sync.isError && (
        <div className="inline-alert">
          {sync.error instanceof ApiError ? sync.error.message : "目录同步失败"}
        </div>
      )}
      {!catalog.data?.length ? (
        <EmptyState
          eyebrow="还没有目录"
          title="把 TED 放映单带到本地"
              body="点击同步后，云端会读取 TED 官网的最新演讲。手机无需访问 YouTube。"
          action={
            <Button
              variant="primary"
              icon={<Sparkles size={16} />}
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              同步 TED 目录
            </Button>
          }
        />
      ) : (
        <section className="catalog-list">
          {catalog.data.map((item, index) => (
            <article className="catalog-row" key={item.id}>
              <span className="catalog-index">{String(index + 1).padStart(2, "0")}</span>
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="catalog-thumb"
                aria-label={`在浏览器打开 ${item.title}`}
              >
                {item.thumbnailUrl ? (
                  <img src={`/api/catalog/${item.id}/thumbnail`} alt="" loading="lazy" />
                ) : (
                  <span>T</span>
                )}
              </a>
              <div className="catalog-copy">
                <span className="catalog-kicker">
                  {item.sourceType === "ted" ? "TED 官网" : "YouTube"} ·{" "}
                  {item.speaker} · {formatDuration((item.durationSec ?? 0) * 1000, true)}
                </span>
                <h2>{item.title}</h2>
                {item.titleZh && (
                  <p className="catalog-title-zh">{item.titleZh}</p>
                )}
                <p>{item.description || "TED 演讲"}</p>
              </div>
              <div className="catalog-actions">
                {item.inLibrary ? (
                  <span className="already-added">
                    <Check size={15} />
                    已在学习库
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    icon={<Download size={15} />}
                    loading={add.isPending && add.variables === item.id}
                    onClick={() => add.mutate(item.id)}
                  >
                    加入学习库
                  </Button>
                )}
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="icon-button"
                  aria-label="在来源网站打开"
                >
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </section>
      )}

      <div className="discover-note">
        <span>来源说明</span>
        <p>
          目录与媒体仅用于你个人的离线化学习。应用不会把下载的视频重新发布或提供给其他用户。
        </p>
        <Link to="/">返回学习库</Link>
      </div>

      {add.isError && (
        <button
          type="button"
          className="toast"
          role="alert"
          onClick={() => add.reset()}
        >
          <span>
            {add.error instanceof ApiError
              ? add.error.message
              : "加入学习库失败，请稍后重试。"}
          </span>
          <X size={15} />
        </button>
      )}
    </div>
  );
}

